import React from "react";
import Dialog from "../../../components/ui/Dialog";
import { useAttendanceLocations } from "../../../api/queries/attendanceQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { formatPlainDate, formatTime } from "../../../utils/dateTime";
import SessionStatusBadge from "./SessionStatusBadge";
import { formatDistance, sessionNotes } from "./attendanceFormat";

/** One check-in or check-out point: time, distance, accuracy, outside flag, map link. */
const PointRow = ({ label, time, point }) => {
  const hasCoords =
    point && point.latitude !== null && point.latitude !== undefined;
  return (
    <div className="loc-point">
      <div className="loc-point-label">{label}</div>
      <div className="fw-semibold">{time ? formatTime(time) : "—"}</div>
      {hasCoords ? (
        <>
          <div
            className={`fs-075 ${point.outsideBranch ? "text-danger fw-semibold" : "text-slate-600"}`}
          >
            {point.distance !== null && point.distance !== undefined
              ? `${formatDistance(point.distance)} from branch`
              : "Distance unknown"}
            {point.outsideBranch && " · outside radius"}
          </div>
          <div className="fs-070 text-slate-500">
            ±
            {point.accuracy !== null && point.accuracy !== undefined
              ? Math.round(point.accuracy)
              : "?"}{" "}
            m accuracy
          </div>
          {point.mapUrl && (
            <a
              href={point.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="fs-075"
            >
              <i className="bi bi-geo-alt me-1"></i>Open in Maps
            </a>
          )}
        </>
      ) : (
        <div className="fs-075 text-slate-400">
          {time ? "No location recorded" : ""}
        </div>
      )}
    </div>
  );
};

/**
 * Check-in / check-out locations of one attendance day (GET /attendance/:id/locations).
 * No map library in the project, so each point links to Google Maps instead.
 */
const LocationsModal = ({ attendanceId, onClose }) => {
  const query = useAttendanceLocations(attendanceId);
  const info = query.data;
  return (
    <Dialog
      title="Check-in locations"
      subtitle={
        info
          ? `${info.employee?.name || ""} · ${info.employee?.employeeNumber || ""} · ${formatPlainDate(info.date)}`
          : undefined
      }
      size="lg"
      onClose={onClose}
    >
      {query.isLoading && (
        <div className="text-center py-4">
          <span
            className="spinner-border text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      )}
      {query.isError && (
        <div className="alert alert-danger fs-085">
          {getApiErrorMessage(query.error, "Could not load the locations.")}
        </div>
      )}
      {info && (
        <>
          <div className="loc-branch">
            <i className="bi bi-building"></i>
            {info.branch ? (
              <div>
                <strong>{info.branch.name}</strong>
                <span className="text-slate-600">
                  {" "}
                  · allowed radius{" "}
                  {info.branch.radius
                    ? formatDistance(info.branch.radius)
                    : "not set"}
                </span>
                {info.branch.mapUrl && (
                  <a
                    href={info.branch.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ms-2 fs-075"
                  >
                    Branch on map
                  </a>
                )}
                {(info.branch.latitude === null ||
                  info.branch.latitude === undefined) && (
                  <div className="fs-075 text-warning-emphasis">
                    This branch has no location yet, so distances can't be
                    checked.
                  </div>
                )}
              </div>
            ) : (
              <span className="text-slate-500">No branch</span>
            )}
          </div>
          {(info.sessions || []).length === 0 && (
            <p className="fs-085 text-slate-500 mb-0">
              No check-ins on this day.
            </p>
          )}
          <ol className="loc-sessions">
            {(info.sessions || []).map((s, i) => (
              <li
                key={s.id || i}
                className={s.status === "REJECTED" ? "is-rejected" : ""}
              >
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <strong className="fs-085">Period {i + 1}</strong>
                  <SessionStatusBadge
                    status={s.status}
                    reason={s.statusReason}
                  />
                  {s.source && (
                    <span className="chip chip--sm">{s.source}</span>
                  )}
                  {s.statusReason && (
                    <span className="fs-075 text-slate-600">
                      {s.statusReason}
                    </span>
                  )}
                </div>
                <div className="loc-points">
                  <PointRow
                    label="Check-in"
                    time={s.checkInTime}
                    point={s.checkIn}
                  />
                  <PointRow
                    label="Check-out"
                    time={s.checkOutTime}
                    point={s.checkOut}
                  />
                </div>
                {sessionNotes(s) && (
                  <div className="fs-075 text-slate-600 mt-1">
                    <i className="bi bi-chat-left-text me-1"></i>
                    {sessionNotes(s)}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </>
      )}
    </Dialog>
  );
};

export default LocationsModal;
